<template>
  <div class="gam-input-search">
    <GamInput
      ref="searchInput"
      v-bind="getSearchInput"
      v-model="query"
      :on-click-clear="handleClear"
      @clear="clearInput"
      @close="closeDropdown"
      @click-message="onClickMessage"
      @focus="onFocus"
    />

    <GamSimpleDropdown
      v-if="getDropdown && !!query && !!items.length && isDropdownOpen"
      v-bind="getDropdown"
      @update="updateSelected"
    >
      <template #dropdown-item="{ item }">
        <GamPlayerCard
          :player="item"
          :size="GamPlayerCardSize.SMALL"
          ref="itemRef"
          :selected="selectedItem?.id === item.id"
          @click-player="handleOrganizerClick"
        />
      </template>
    </GamSimpleDropdown>
  </div>
</template>

<script setup lang="ts">
import type { BaseUserDto } from '@/core/data/user/user.interface';
import type { GamSimpleDropdownType } from '@/views/composables/models/components/GamDropdown';
import type { GamInputType, GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import { debounce } from 'lodash';
import { computed, nextTick, onBeforeMount, onUnmounted, ref, watch } from 'vue';
import { GamPlayerCardSize } from '../composables/constants/components/gamPlayer.constants';
import GamInput from './GamInput.vue';
import GamSimpleDropdown from './GamSimpleDropdown.vue';
import GamPlayerCard from './gam-player/GamPlayerCard.vue';

// TODO: Accept a list of existing organizers to exclude from the loadCallback results
const props = defineProps<
  GamSimpleInputSearchType<BaseUserDto, BaseUserDto, GamSimpleDropdownType<BaseUserDto>> & {
    modelValue?: string | number | null;
  }
>();
const emit = defineEmits(['update:modelValue']);

const normalizeQuery = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const internalQuery = ref<string>(
  normalizeQuery(props.modelValue !== undefined ? props.modelValue : props.input.value),
);
const query = computed<string>({
  get: () => internalQuery.value,
  set: (value) => {
    const normalized = normalizeQuery(value);
    internalQuery.value = normalized;
    emit('update:modelValue', normalized);
  },
});

watch(
  () => props.modelValue,
  (value) => {
    if (value === undefined) return;
    internalQuery.value = normalizeQuery(value);
  },
);

watch(
  () => props.input.value,
  (value) => {
    if (props.modelValue !== undefined) return;
    internalQuery.value = normalizeQuery(value);
  },
);
const items = ref<BaseUserDto[]>([]);
const infiniteLoad = () => {};

const searchInput = ref();
const loadedOnInit = ref<boolean>(false);
const tempSelectedItem = ref<BaseUserDto | null>(null);
const selectedItem = ref<BaseUserDto | null>(null);
const isLoaded = ref<boolean>(false);

onBeforeMount(async () => {
  setDefaultItem();

  if (props.loadCallback) {
    if (props.loadOnInit) {
      await initLoad();
    } else {
      tempSelectedItem.value = selectedItem.value;
      loadedOnInit.value = true;
    }
  }
});

const initLoad = async () => {
  loadedOnInit.value = true;
};

const handleOrganizerClick = (item: BaseUserDto) => {
  if (!item.username || !props.selectCallback) {
    return;
  }

  props.selectCallback(item);
};

const excludeAtSign = (value: string): string => {
  if (value.startsWith('@')) {
    return value.slice(1);
  }
  return value;
};

const isSearchEntered = (value: string = query.value): boolean => {
  const inputValue = excludeAtSign(normalizeQuery(value)) || '';
  return inputValue.length >= 2 && !isDefaultValue(value);
};

const setDefaultItem = (): void => {
  if (props.dropdown?.selected) {
    selectedItem.value = props.dropdown.selected;
  } else if (props.dropdown?.defaultItem) {
    selectedItem.value = props.dropdown.defaultItem;
  } else if (tempSelectedItem.value) {
    selectedItem.value = tempSelectedItem.value;
  }
};

const isDefaultSelected = (): boolean => {
  return !!props.dropdown?.defaultItem && selectedItem.value?.id === props.dropdown?.defaultItem?.id;
};

const isDefaultValue = (value: string = query.value): boolean => {
  return excludeAtSign(normalizeQuery(value)) === props.dropdown?.defaultItem?.username;
};

const closeDropdown = () => {
  if (searchInput.value && props.dropdown) {
    searchInput.value.isActive = false;
  }
};

const clearInput = () => {
  if (props.input.canClear) {
    query.value = '';
    tempSelectedItem.value = null;
  }
};

const handleClear = () => {
  props.onClickClear?.();
  searchInput.value?.focus?.();
};

const isDropdownOpen = computed((): boolean => {
  if (!props.dropdown) {
    return false;
  }

  if (!query.value) {
    return false;
  }

  if (props.loadOnInit) {
    return true;
  }

  if (!searchInput.value?.isActive) {
    return false;
  }

  if (!Array.isArray(items.value) || (items.value.length < 1 && props.isLoading === true)) {
    return false;
  }

  if (isSearchEntered()) {
    return true;
  }

  return false;
});

const getSearchInput = computed((): GamInputType => {
  return {
    ...props.input,
    name: 'follower',
    value: query.value,
    isDropdownOpen: props.dropdown && isDropdownOpen.value,
    placeholder: props.input.placeholder || 'groups.organizers.search.placeholder',
    onMounted: props.onMounted,
  };
});

const getDropdown = computed((): GamSimpleDropdownType<BaseUserDto> | undefined => {
  return {
    ...props.dropdown,
    query: excludeAtSign(query.value),
    items: items.value as any as BaseUserDto[],
    infiniteLoad,
    isOpen: isDropdownOpen.value,
    isLoading: props.isLoading,
    maxItems: 5,
  };
});

const updateSelected = async (result?: BaseUserDto | null) => {
  if (!result) return;

  if (props.selectCallback) {
    props.selectCallback(result);

    return;
  }

  tempSelectedItem.value = result;
  query.value = result.username;

  closeDropdown();
};

// TODO: This gets called when blurring a cleared input
watch(selectedItem, async (selected: BaseUserDto | null) => {
  if (props.selectCallback) {
    props.selectCallback(selected);
  }
  await updateSelected(selected);
});

watch(isDropdownOpen, async (isOpen: boolean) => {
  if (props.dropdown) {
    if (isOpen) {
      await nextTick(async () => {
        if (props.loadOnInit && !loadedOnInit.value) {
          await initLoad();
        } else {
          isLoaded.value = true;
          loadedOnInit.value = true;
        }
      });
    } else if (loadedOnInit.value) {
      if (selectedItem.value) {
        if (isDefaultSelected()) {
          const newQuery = props.dropdown?.defaultItem?.username || '';
          query.value = newQuery;
        }
      }
    }
  }
});

type LoadItemsOptions = {
  query: string;
};

async function loadItems({ query }: LoadItemsOptions) {
  const results = await props.loadCallback?.(query);

  if (!results?.data) {
    items.value = [];
    return;
  }

  items.value = results.data;
}

const debouncedQuery = debounce(async (nextQuery: string) => {
  if (isSearchEntered(nextQuery) && loadedOnInit.value) {
    const sanitizedQuery = excludeAtSign(nextQuery);
    await props.onQueryChanged?.(sanitizedQuery);

    await loadItems({ query: sanitizedQuery });
  } else if (!isSearchEntered(nextQuery)) {
    await props.onQueryChanged?.(undefined);
  }
}, 400);

watch(
  () => (props.modelValue !== undefined ? props.modelValue : internalQuery.value),
  async (nextQuery, prevQuery) => {
    if (nextQuery === prevQuery) return;

    debouncedQuery.cancel();
    debouncedQuery(normalizeQuery(nextQuery));
  },
);

watch(
  () => searchInput.value?.isActive,
  (value) => {
    if (!value) {
      setDefaultItem();
    }
  },
);

defineExpose({
  clearInput,
  closeDropdown,
  focusInput: () => {
    searchInput.value?.setFocus?.();
  },
  blurInput: () => {
    searchInput.value?.setBlur?.();
  },
});

onUnmounted(async () => {
  tempSelectedItem.value = null;
});
</script>

<style scoped lang="scss">
.gam-input-search {
  display: flex;
  position: relative;
  align-items: flex-start;
  align-self: stretch;
  flex: 1 0 0;
}

.gam-simple-input {
  width: 100%;
}
</style>
